import React, { useState } from "react";

import { useTranslation } from "utils";

import { CurrencyCode } from "consts";

import { Payment, SingleFieldOutcomes } from "models";

import { Drawer, ExtraInfo, Flex } from "components";

import { PayoutContextBaselineCard } from "pages/app/field-timeline/components/payout-context-drawer/PayoutContextBaselineCard";
import { PayoutContextCalculationCard } from "pages/app/field-timeline/components/payout-context-drawer/PayoutContextCalculationCard";
import { PayoutContextStructureCard } from "pages/app/field-timeline/components/payout-context-drawer/PayoutContextStructureCard";
import { PayoutContextSummaryCard } from "pages/app/field-timeline/components/payout-context-drawer/PayoutContextSummaryCard";
import { PayoutContextTimelineCard } from "pages/app/field-timeline/components/payout-context-drawer/PayoutContextTimelineCard";

type PayoutContextDrawerProps = {
  visible: boolean;
  onClose: () => void;
  reportingYear: number;
  monitoringYears: number[];
  currency?: CurrencyCode;
  actualPayout: number;
  eligiblePayout: number;
  totalPayoutMax: number;
  co2Actual: number;
  co2Baseline: number;
  hasDataEntry: boolean;
  allFieldOutcomes: SingleFieldOutcomes[];
  baselineFootprints: Record<number, number>;
  farmPayments: Payment[];
  hasNoAllotment: boolean;
};

export const PayoutContextDrawer = ({
  visible,
  onClose,
  reportingYear,
  monitoringYears,
  currency,
  actualPayout,
  eligiblePayout,
  totalPayoutMax,
  co2Baseline,
  allFieldOutcomes,
  baselineFootprints,
  farmPayments,
  hasNoAllotment,
}: PayoutContextDrawerProps) => {
  const [showIncreaseEligibleDrawer, setShowIncreaseEligibleDrawer] = useState(false);
  const { t } = useTranslation("fieldTimeline");

  const fallbackPricePerT = 30;
  const hasAllotment = eligiblePayout > 0 && co2Baseline > 0;
  const pricePerT = hasAllotment ? eligiblePayout / co2Baseline : fallbackPricePerT;
  const maxEligibleImpact = hasAllotment ? co2Baseline : 200;
  const structureEligiblePayout = hasAllotment ? eligiblePayout : 6000;

  return (
    <>
      <Drawer
        className="payout-context-drawer"
        dataTestId="payout-context-drawer"
        title={t("impactPanel.payoutImpact.contextDrawer.title")}
        visible={visible}
        onClose={onClose}
        zIndex={10003}
      >
        <Flex vertical className="payout-context-drawer-content" gap="1rem">
          <PayoutContextSummaryCard currency={currency} actualPayout={actualPayout} eligiblePayout={eligiblePayout} hasNoAllotment={hasNoAllotment} />

          <PayoutContextTimelineCard
            currency={currency}
            actualPayout={actualPayout}
            eligiblePayout={eligiblePayout}
            allFieldOutcomes={allFieldOutcomes}
            farmPayments={farmPayments}
            totalPayoutMax={totalPayoutMax}
            reportingYear={reportingYear}
            monitoringYears={monitoringYears}
          />

          {hasAllotment ? (
            <PayoutContextStructureCard
              currency={currency}
              eligiblePayout={structureEligiblePayout}
              pricePerT={pricePerT}
              maxEligibleImpact={maxEligibleImpact}
              totalPayoutMax={totalPayoutMax}
              onLearnMore={() => setShowIncreaseEligibleDrawer(true)}
            />
          ) : null}

          {hasAllotment ? (
            <PayoutContextCalculationCard
              currency={currency}
              co2Baseline={co2Baseline}
              hasAllotment={hasAllotment}
              pricePerT={pricePerT}
              actualPayout={actualPayout}
            />
          ) : null}

          <PayoutContextBaselineCard baselineFootprints={baselineFootprints} />
        </Flex>
      </Drawer>

      <ExtraInfo setVisible={setShowIncreaseEligibleDrawer} type="increase-eligible-payout" visible={showIncreaseEligibleDrawer} zIndex={10004} />
    </>
  );
};
